import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSafeBack } from "../hooks/useSafeBack";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Copy, Loader2, UserPlus, MessageSquare,
  Users, TrendingDown, FileText, Eye, ChevronRight, X,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { MobileDialog } from "../components/mobile";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * BetaAdmin — admin-only. Issue invite codes (Wave 1 ADI cap = 3), list
 * issued codes, and view incoming beta feedback. Deliberately minimal per
 * sprint rules: no vanity UI, just what's useful for running the rollout.
 */
export default function BetaAdmin() {
  const nav = useNavigate();
  const goBack = useSafeBack();
  const [invites, setInvites] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [testers, setTesters] = useState({ summary: {}, testers: [] });
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [role, setRole] = useState("adi");
  const [wave, setWave] = useState("wave1");
  const [inviteEmail, setInviteEmail] = useState("");
  const [note, setNote] = useState("");
  const [launchLock, setLaunchLock] = useState(null);
  const [noteDrafts, setNoteDrafts] = useState({});      // per-tester note composer
  const [savingNoteFor, setSavingNoteFor] = useState("");
  const [supportFor, setSupportFor] = useState(null);    // tester being inspected
  const [support, setSupport] = useState(null);

  const load = useCallback(async () => {
    try {
      const [i, f, t, l] = await Promise.all([
        axios.get(`${API}/api/beta/admin/invites`, { withCredentials: true }),
        axios.get(`${API}/api/beta/admin/feedback`, { withCredentials: true }),
        axios.get(`${API}/api/beta/admin/testers`, { withCredentials: true }),
        axios.get(`${API}/api/launch-lock/admin/state`, { withCredentials: true }),
      ]);
      setInvites(i.data.items || []);
      setFeedback(f.data.items || []);
      setTesters(t.data || { summary: {}, testers: [] });
      setLaunchLock(l.data || null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setCreating(true);
    try {
      await axios.post(
        `${API}/api/beta/admin/invites`,
        { role, wave, email: inviteEmail.trim() || null, note },
        { withCredentials: true }
      );
      toast.success("Invite created");
      setNote("");
      setInviteEmail("");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Unable to create");
    } finally {
      setCreating(false);
    }
  };

  const copy = (code) => {
    navigator.clipboard.writeText(code);
    toast.success(`Copied ${code}`);
  };

  const addNote = async (uid) => {
    const draft = (noteDrafts[uid] || "").trim();
    if (!draft) return;
    setSavingNoteFor(uid);
    try {
      await axios.post(
        `${API}/api/beta/admin/notes`,
        { user_id: uid, note: draft },
        { withCredentials: true },
      );
      setNoteDrafts((d) => ({ ...d, [uid]: "" }));
      toast.success("Note added");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save note");
    } finally {
      setSavingNoteFor("");
    }
  };

  const openSupport = async (tester) => {
    setSupportFor(tester);
    setSupport(null);
    try {
      const { data } = await axios.get(
        `${API}/api/beta/admin/support/${tester.user_id}`,
        { withCredentials: true },
      );
      setSupport(data);
    } catch (err) {
      toast.error("Unable to load support data");
    }
  };

  if (loading) return (
    <div className="min-h-dvh no-overflow-x flex items-center justify-center bg-slate-50 dark:bg-[#0c1120]">
      <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
    </div>
  );

  const adiRedeemed = invites.filter((i) => i.role === "adi" && i.redeemed_at).length;

  return (
    <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-[#0c1120] pb-24">
      <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 sticky top-0 z-30">
        <div className="max-w-2xl mx-auto px-4 h-14 flex items-center gap-3">
          <button data-testid="beta-admin-back-btn" onClick={() => goBack()} className="text-slate-400 hover:text-slate-600">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <UserPlus className="w-5 h-5 text-indigo-600" />
          <p className="font-extrabold text-slate-900 dark:text-white text-sm flex-1">Beta Rollout</p>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 pt-4 space-y-4">
        {/* Stats strip — invited / activated / churned */}
        <div className="grid grid-cols-4 gap-2" data-testid="beta-stats">
          <StatTile testId="stat-invited" icon={<UserPlus className="w-3.5 h-3.5 text-slate-500" />} label="Invited" value={testers.summary?.invited ?? 0} />
          <StatTile testId="stat-activated" icon={<Users className="w-3.5 h-3.5 text-emerald-500" />} label="Activated" value={testers.summary?.activated ?? 0} tone="emerald" />
          <StatTile testId="stat-active" icon={<ChevronRight className="w-3.5 h-3.5 text-blue-500" />} label="Active" value={testers.summary?.active ?? 0} tone="blue" />
          <StatTile testId="stat-churned" icon={<TrendingDown className="w-3.5 h-3.5 text-rose-500" />} label="Churned" value={testers.summary?.churned ?? 0} tone="rose" />
        </div>

        <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5">
          <p className="text-sm font-extrabold text-slate-900 dark:text-white mb-1">Issue invite</p>
          <p className="text-[11px] text-slate-500 mb-3">
            Wave 1 ADI cap: <b>{adiRedeemed} / 3</b> redeemed. Beta testers get a 30-day trial + 7-day founder-lock window.
          </p>
          <div className="grid grid-cols-3 gap-2 mb-3">
            <select data-testid="beta-invite-role" value={role} onChange={(e) => setRole(e.target.value)} className="h-10 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 text-sm">
              <option value="adi">ADI</option>
              <option value="pdi">PDI</option>
              <option value="learner">Learner</option>
            </select>
            <input data-testid="beta-invite-wave" value={wave} onChange={(e) => setWave(e.target.value)} placeholder="wave1" className="h-10 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 text-sm" />
            <Button data-testid="beta-invite-create" onClick={create} disabled={creating} className="h-10 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-bold">
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> Issue</>}
            </Button>
          </div>
          <input data-testid="beta-invite-email" type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="Optional email (for tracking)" className="w-full h-10 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 text-sm mb-2" />
          <input data-testid="beta-invite-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional note (e.g. 'Sam — Leeds')" className="w-full h-10 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 text-sm" />
        </div>

        {launchLock && (
          <LaunchLockPanel state={launchLock} onChange={load} />
        )}

        <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5">
          <p className="text-sm font-extrabold text-slate-900 dark:text-white mb-3">Invites ({invites.length})</p>
          {invites.length === 0 ? (
            <p className="text-sm text-slate-500 py-3">No invites yet.</p>
          ) : (
            <div className="space-y-2">
              {invites.map((i) => (
                <div key={i.code} data-testid={`beta-invite-${i.code}`} className="flex items-center gap-3 rounded-lg border border-slate-200 dark:border-slate-700 p-2">
                  <span className="font-mono text-xs font-bold text-slate-900 dark:text-white flex-1">{i.code}</span>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200">{i.role}</span>
                  <span className="text-[10px] text-slate-400">{i.wave}</span>
                  {i.redeemed_at ? (
                    <span className="text-[10px] font-bold text-emerald-600">redeemed</span>
                  ) : (
                    <button data-testid={`beta-copy-${i.code}`} onClick={() => copy(i.code)} className="text-slate-400 hover:text-slate-600">
                      <Copy className="w-3 h-3" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5" data-testid="testers-panel">
          <p className="text-sm font-extrabold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
            <Users className="w-4 h-4 text-slate-400" /> Active testers ({testers.testers?.length || 0})
          </p>
          {(!testers.testers || testers.testers.length === 0) ? (
            <p className="text-sm text-slate-500 py-3">No activated testers yet. Share invite codes above.</p>
          ) : (
            <div className="space-y-3">
              {testers.testers.map((t) => (
                <div key={t.user_id} data-testid={`tester-row-${t.user_id}`} className="rounded-xl border border-slate-200 dark:border-slate-700 p-3 space-y-2">
                  <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-extrabold text-slate-900 dark:text-white truncate">
                        {t.name || t.email}
                        {t.churned && <span className="ml-2 text-[10px] font-bold text-rose-600 bg-rose-100 dark:bg-rose-900/30 px-1.5 py-0.5 rounded-full">churned</span>}
                      </p>
                      <p className="text-[11px] text-slate-400 truncate">{t.email} · {t.role} · {t.wave}</p>
                      <p className="text-[11px] text-slate-500 mt-0.5">
                        {t.events_7d} events (7d) · last: {t.last_testid || t.last_event || "—"}
                        {t.nps_score != null && <span> · NPS {t.nps_score}</span>}
                      </p>
                      <p className="text-[10.5px] mt-1" data-testid={`tester-trial-${t.user_id}`}>
                        {t.founder_lock ? (
                          <span className="font-bold text-emerald-600">🔒 Founder lock active</span>
                        ) : t.trial_expired ? (
                          <span className="font-bold text-rose-600">Trial expired</span>
                        ) : t.trial_days_remaining > 0 ? (
                          <span className="font-bold text-[#b8860b]">{t.trial_days_remaining}d trial remaining</span>
                        ) : (
                          <span className="text-slate-400">No trial set</span>
                        )}
                        <span className="text-slate-400"> · plan: {t.plan || "free"}</span>
                      </p>
                    </div>
                    <button
                      data-testid={`tester-support-${t.user_id}`}
                      onClick={() => openSupport(t)}
                      className="text-xs font-bold text-indigo-600 hover:text-indigo-700 flex items-center gap-1 flex-shrink-0"
                    >
                      <Eye className="w-3 h-3" /> Support
                    </button>
                  </div>

                  {t.notes?.length > 0 && (
                    <div className="space-y-1 pl-2 border-l-2 border-slate-200 dark:border-slate-700">
                      {t.notes.slice(0, 3).map((n, i) => (
                        <p key={i} className="text-[11px] text-slate-600 dark:text-slate-300">
                          <FileText className="inline w-3 h-3 mr-1 text-slate-400" />
                          {n.note}
                        </p>
                      ))}
                    </div>
                  )}

                  <div className="flex gap-1.5">
                    <input
                      data-testid={`tester-note-draft-${t.user_id}`}
                      value={noteDrafts[t.user_id] || ""}
                      onChange={(e) => setNoteDrafts((d) => ({ ...d, [t.user_id]: e.target.value }))}
                      onKeyDown={(e) => { if (e.key === "Enter") addNote(t.user_id); }}
                      placeholder="Add a note (onboarding call summary, blockers…)"
                      className="flex-1 h-8 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-2.5 text-xs"
                    />
                    <button
                      data-testid={`tester-note-add-${t.user_id}`}
                      onClick={() => addNote(t.user_id)}
                      disabled={savingNoteFor === t.user_id || !(noteDrafts[t.user_id] || "").trim()}
                      className="h-8 px-2.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold disabled:opacity-50"
                    >
                      {savingNoteFor === t.user_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5">
          <p className="text-sm font-extrabold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-slate-400" /> Feedback ({feedback.length})
          </p>
          {feedback.length === 0 ? (
            <p className="text-sm text-slate-500 py-3">No feedback yet.</p>
          ) : (
            <div className="space-y-2">
              {feedback.slice(0, 30).map((f) => (
                <div key={f.id} data-testid={`beta-feedback-${f.id}`} className="rounded-lg border border-slate-200 dark:border-slate-700 p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700">{f.category}</span>
                    <span className="text-xs font-bold text-slate-900 dark:text-white">{"★".repeat(f.rating)}</span>
                    <span className="text-[10px] text-slate-400 ml-auto">{f.role}</span>
                  </div>
                  {f.message && <p className="text-sm text-slate-700 dark:text-slate-300">{f.message}</p>}
                  {f.context_url && <p className="text-[10px] text-slate-400 mt-1">{f.context_url}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Support drawer */}
      {supportFor && (
        <MobileDialog
          bareContent
          open={!!supportFor}
          onClose={() => { setSupportFor(null); setSupport(null); }}
          testid="support-drawer"
          priority="urgent"
        >
          <div
            className="w-full sm:max-w-lg bg-white dark:bg-slate-900 rounded-t-3xl sm:rounded-3xl shadow-2xl border-t sm:border border-slate-200 dark:border-slate-800 flex flex-col"
          >
            <div className="px-5 pt-4 pb-3 border-b border-slate-100 dark:border-slate-800 flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-extrabold text-slate-900 dark:text-white truncate">Support · {supportFor.name || supportFor.email}</p>
                <p className="text-[11px] text-slate-500 truncate">{supportFor.email} · {supportFor.role}</p>
              </div>
              <button data-testid="support-drawer-close" onClick={() => { setSupportFor(null); setSupport(null); }} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
              {!support ? (
                <div className="py-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-indigo-500" /></div>
              ) : (
                <>
                  <div data-testid="support-last-page">
                    <p className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-1">Last page viewed</p>
                    {support.last_page ? (
                      <p className="text-xs text-slate-700 dark:text-slate-300">
                        <code className="font-mono">{support.last_page.metadata?.path || support.last_page.testid || "/"}</code>
                        <span className="text-slate-400"> · {new Date(support.last_page.created_at).toLocaleString()}</span>
                      </p>
                    ) : (
                      <p className="text-xs text-slate-400 italic">No page_view events logged.</p>
                    )}
                  </div>
                  <div data-testid="support-recent-clicks">
                    <p className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-1">Recent clicks</p>
                    {support.recent_clicks?.length === 0 ? (
                      <p className="text-xs text-slate-400 italic">No clicks logged.</p>
                    ) : (
                      <ul className="space-y-1">
                        {support.recent_clicks.map((c, i) => (
                          <li key={i} className="flex items-center justify-between text-xs">
                            <code className="font-mono text-slate-700 dark:text-slate-300 truncate">{c.testid || c.event}</code>
                            <span className="text-[10px] text-slate-400 flex-shrink-0">{new Date(c.created_at).toLocaleTimeString()}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </MobileDialog>
      )}
    </div>
  );
}

function StatTile({ testId, icon, label, value, tone = "slate" }) {
  const classes = {
    slate: "bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white",
    emerald: "bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300",
    blue: "bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300",
    rose: "bg-rose-50 dark:bg-rose-900/20 text-rose-700 dark:text-rose-300",
  }[tone] || "bg-slate-50 text-slate-900";
  return (
    <div data-testid={testId} className={`rounded-xl p-2.5 ${classes}`}>
      <div className="flex items-center gap-1 text-[10px] uppercase font-bold opacity-80">
        {icon}
        <span>{label}</span>
      </div>
      <p className="text-xl font-extrabold mt-0.5">{value}</p>
    </div>
  );
}

function LaunchLockPanel({ state, onChange }) {
  const [capDraft, setCapDraft] = useState(state.cap);
  const [saving, setSaving] = useState(false);

  const save = async (overrides = {}) => {
    setSaving(true);
    try {
      await axios.post(
        `${API}/api/launch-lock/admin/configure`,
        { cap: capDraft, ...overrides },
        { withCredentials: true }
      );
      toast.success("Launch lock updated");
      await onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    } finally {
      setSaving(false);
    }
  };

  const pct = state.cap > 0 ? Math.round((state.claimed / state.cap) * 100) : 0;

  return (
    <div data-testid="launch-lock-admin-panel" className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5">
      <div className="flex items-start justify-between gap-2 mb-3">
        <div>
          <p className="text-sm font-extrabold text-slate-900 dark:text-white">Launch Member Lock</p>
          <p className="text-[11px] text-slate-500">
            First {state.cap} instructors lock {state.founder_lock_pct}% off for life.
          </p>
        </div>
        <span data-testid="launch-lock-active-pill" className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${state.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>
          {state.active ? "Live" : "Closed"}
        </span>
      </div>
      <div className="mb-3">
        <div className="flex items-center justify-between text-[11px] mb-1">
          <span className="text-slate-700 dark:text-slate-200 font-bold">{state.claimed} / {state.cap} claimed</span>
          <span className="text-slate-400">{pct}%</span>
        </div>
        <div className="h-2 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
          <div className="h-full rounded-full bg-gradient-to-r from-amber-300 to-amber-500" style={{ width: `${Math.max(2, pct)}%` }} />
        </div>
        <p className="text-[10.5px] text-slate-500 mt-1">
          Total founder-locked users (any source): <b>{state.locked_users_total}</b>
        </p>
      </div>
      <div className="flex items-center gap-2">
        <input data-testid="launch-lock-cap-input" type="number" min={1} value={capDraft} onChange={(e) => setCapDraft(parseInt(e.target.value, 10) || 1)} className="w-24 h-9 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 text-sm" />
        <Button data-testid="launch-lock-save-cap" onClick={() => save()} disabled={saving} size="sm" className="h-9 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold">
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : "Set cap"}
        </Button>
        {state.active ? (
          <Button data-testid="launch-lock-close" onClick={() => save({ active: false })} disabled={saving} size="sm" variant="outline" className="h-9 rounded-lg text-xs font-bold ml-auto">
            Close
          </Button>
        ) : (
          <Button data-testid="launch-lock-reopen" onClick={() => save({ active: true })} disabled={saving} size="sm" className="h-9 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold ml-auto">
            Reopen
          </Button>
        )}
      </div>
    </div>
  );
}
